import { supabase } from './supabase';
import { loadVendorNames } from './vendors';

// Seed ingredient categories for a new org from the global template
async function seedOrgCategories(orgId) {
  const { data: existing } = await supabase.from('ingredient_categories').select('id').eq('org_id', orgId).limit(1);
  if (existing?.length) return; // already has categories
  const { data: globals } = await supabase.from('ingredient_categories').select('name, sort_order').is('org_id', null).order('sort_order');
  if (!globals?.length) return;
  const rows = globals.map(g => ({ org_id: orgId, name: g.name, sort_order: g.sort_order }));
  await supabase.from('ingredient_categories').insert(rows);
}

export async function loadUserData(targetOrgId, salesDate) {
  const { data: { user } } = await supabase.auth.getUser();
  const { data: memberships } = await supabase
    .from('org_members').select('*').eq('user_id', user.id);

  // Pick target org or fall back to first membership
  const orgMembership = (targetOrgId && memberships?.find(m=>m.org_id===targetOrgId)) || memberships?.[0] || null;
  const orgId = orgMembership?.org_id || null;
  const userRole = orgMembership?.role || null;
  if (!orgId) console.error('loadUserData: no org membership found for user', user.id, 'memberships:', memberships);
  if (orgId) await seedOrgCategories(orgId);
  // Toast staleness check — runs on every app load, independent of cron
  // Wrapped in try/catch so a failure here never blocks app load
  let toastSyncStale = false;
  let toastEnabled = false;
  if (orgId) {
    try {
    const { data: toastCfg } = await supabase.from('toast_config').select('enabled').eq('org_id', orgId).single();
    toastEnabled = !!(toastCfg?.enabled);
    if (toastEnabled) {
      const { data: latestToast } = await supabase.from('sales')
        .select('date').eq('org_id', orgId).eq('source', 'toast')
        .order('date', { ascending: false }).limit(1).single();
      if (latestToast) {
        const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
        const daysStale = Math.round((new Date(today) - new Date(latestToast.date)) / 86400000);
        if (daysStale > 2) {
          toastSyncStale = true;
          // Create task if none pending
          const { data: existingTask } = await supabase.from('tasks')
            .select('id').eq('org_id', orgId).eq('type', 'toast_sync_stale').eq('status', 'pending').limit(1).single();
          if (!existingTask) {
            await supabase.from('tasks').insert({
              org_id: orgId, type: 'toast_sync_stale', status: 'pending',
              title: `Toast sales sync stale — last data is ${latestToast.date}`,
              note: `Most recent Toast-sourced sales date is ${latestToast.date} (${daysStale} days ago). Check if the nightly sync is running.`,
              source: 'system', priority: 8,
            });
          }
        }
      }
    }
    } catch (e) { console.warn('[loadData] Toast staleness check failed (non-blocking):', e.message); }
  }
  const canManageTeam = userRole === 'owner' || userRole === 'admin' || orgMembership?.can_manage_team || false;

  // Load org names for switcher
  let allOrgs = [];
  if (memberships?.length > 1) {
    const orgIds = memberships.map(m => m.org_id);
    const { data: orgsData } = await supabase.from('organizations').select('id,name,onboarding_complete').in('id', orgIds);
    allOrgs = (orgsData || []).map(o => ({ id: o.id, name: o.name, role: memberships.find(m => m.org_id === o.id)?.role, onboarding_complete: o.onboarding_complete }));
  } else if (orgId) {
    const { data: orgRow } = await supabase.from('organizations').select('id,name,onboarding_complete').eq('id', orgId).single();
    if (orgRow) allOrgs = [{ id: orgRow.id, name: orgRow.name, role: userRole, onboarding_complete: orgRow.onboarding_complete }];
  }

  // Load user permissions
  let userPerms = {};
  if (orgId) {
    const { data: permsData } = await supabase.from('user_permissions').select('section,can_view,can_edit').eq('user_id', user.id).eq('org_id', orgId);
    if (permsData) {
      permsData.forEach(p => { userPerms[p.section] = { can_view: p.can_view, can_edit: p.can_edit }; });
    }
  }

  const [
    { data: dbIngs },
    { data: dbBatches },
    { data: dbBI },
    { data: dbMI },
    { data: dbRecipes }, // loaded for future shared-recipe support; yield will move here
    { data: dbRI },
    { data: dbFC },
    { data: dbSettings },
    { data: dbSales },
    { data: dbLabor },
    { data: dbWaste },
    { data: dbPurchases, error: dbPurchasesErr },
    { data: dbOtherExp },
    { data: dbInvCounts },
    { data: dbInvLastCounted },
    { data: dbWholesale },
  ] = await Promise.all([
    supabase.from('ingredients').select('*').eq('org_id', orgId).order('sort_order'),
    supabase.from('batches').select('*').eq('org_id', orgId),
    supabase.from('batch_ingredients').select('*').eq('org_id', orgId).order('sort_order'),
    supabase.from('menu_items').select('*').eq('org_id', orgId),
    supabase.from('recipes').select('*').eq('org_id', orgId),
    supabase.from('recipe_ingredients').select('*').eq('org_id', orgId).order('sort_order'),
    supabase.from('fixed_costs').select('*').eq('org_id', orgId),
    supabase.from('settings').select('*').eq('org_id', orgId).limit(1).single(),
    supabase.from('sales').select('*').eq('org_id', orgId).order('date', { ascending: false }).limit(5000),
    supabase.from('labor').select('*').eq('org_id', orgId).limit(20000),
    supabase.from('waste_log').select('*').eq('org_id', orgId).order('created_at').limit(20000),
    supabase.from('purchases').select('*,invoices!left(grand_total,invoice_number)').eq('org_id', orgId).eq('is_superseded', false).order('date', { ascending: false }).limit(20000),
    supabase.from('categorized_expenses').select('*').eq('org_id', orgId).eq('is_superseded', false).order('date', { ascending: false }).limit(20000),
    supabase.from('inventory_counts').select('ingredient_id,cases,open_qty').eq('org_id', orgId).eq('date', salesDate || new Date().toISOString().split('T')[0]),
    supabase.from('inventory_counts').select('ingredient_id,date').eq('org_id', orgId),
    supabase.from('wholesale_sales').select('*, wholesale_sale_items(*)').eq('org_id', orgId),
  ]);

  console.log('[loadData] purchases query result — count:', dbPurchases?.length ?? 'null', 'error:', dbPurchasesErr);
  if (dbPurchasesErr) console.error('[loadData] purchases error detail:', JSON.stringify(dbPurchasesErr));
  if (dbPurchases?.length) console.log('[loadData] first purchase:', JSON.stringify(dbPurchases[0]));

  // --- Ingredients ---
  const ingredients = dbIngs?.length ? dbIngs.map((r, idx) => ({
    id: r.id, n: r.name, b: r.brand || '', cat: r.category || '', dist: r.distributor || '',
    cq: Number(r.case_qty), cu: r.unit, cp: Number(r.case_price), yp: Number(r.yield_pct),
    tier: r.tier, loc: r.location || 'Dry Storage', par: r.par, pk: r.pack_size, so: r.sort_order ?? idx, vin: r.vendor_item_number || '', active: r.is_active !== false,
    ppc: Number(r.packs_per_case) || 1, spp: Number(r.size_per_pack) || Number(r.case_qty),
    updatedAt: r.updated_at || null, ec: r.expense_category || 'Food',
    pt: r.pack_type || null, slc: r.slices_per_pack || null, srv: r.servings_per_pack || null, sru: r.servings_unit || null, pdu: r.pricing_display_unit || null,
    pcu: r.preferred_cost_unit || null,
    lastPriceDate: r.last_price_date || null,
    categoryId: r.category_id || null, categorySource: r.category_source || null, categoryConfidence: r.category_confidence != null ? Number(r.category_confidence) : null,
  })) : null;

  // --- Batches ---
  let batches = null;
  if (dbBatches?.length) {
    const biByBatch = {};
    (dbBI || []).forEach(r => {
      if (!biByBatch[r.batch_id]) biByBatch[r.batch_id] = [];
      biByBatch[r.batch_id].push({ i: r.ingredient_id || r.sub_batch_id, q: Number(r.qty), u: r.unit });
    });
    batches = dbBatches.map(r => ({
      id: r.id, n: r.name, yield: Number(r.yield), yu: r.yield_unit,
      lastPrice: r.last_price != null ? Number(r.last_price) : null,
      r: biByBatch[r.id] || [],
    }));
  }

  // --- Menu Items ---
  // Build recipe lines lookup: recipe_id → [{i, q, u}]
  const riByRecipe = {};
  let menuItems = null;
  if (dbMI?.length) {
    (dbRI || []).forEach(r => {
      if (!riByRecipe[r.recipe_id]) riByRecipe[r.recipe_id] = [];
      riByRecipe[r.recipe_id].push({ i: r.ingredient_id || r.batch_id, q: Number(r.qty), u: r.unit });
    });
    menuItems = dbMI.filter(r => r.is_active !== false).map(r => ({
      id: r.id, n: r.name, c: r.category, cat: r.category,
      p: Number(r.price), yield: r.yield,
      lastYield: r.last_yield != null ? Number(r.last_yield) : null,
      lastYieldUnit: r.last_yield_unit || null,
      salesCat: r.sales_category || null,
      toastGuid: r.toast_guid || null,
      recipeId: r.recipe_id || null,
      r: r.recipe_id ? (riByRecipe[r.recipe_id] || []) : [],
    }));
  }

  // --- Recipe Templates ---
  const templates = (dbRecipes || []).filter(r => r.is_template).map(r => ({
    id: r.id, n: r.name,
    r: riByRecipe[r.id] || [],
  }));

  // --- Fixed Costs ---
  const fixedCosts = dbFC?.length ? dbFC.map(r => ({
    id: r.id, n: r.name, amt: Number(r.amount), per: r.period, category: r.category || null,
  })) : null;

  // --- Settings ---
  const vendorNames = orgId ? await loadVendorNames(orgId) : [];
  const settings = dbSettings ? {
    bizName: dbSettings.biz_name, bizLoc: dbSettings.biz_loc, bizAddress: dbSettings.biz_address || '', bizEmail: dbSettings.biz_email || '', bizLogoUrl: dbSettings.biz_logo_url || '', weekStart: dbSettings.week_start,
    fcTarget: Number(dbSettings.fc_target), laborTarget: Number(dbSettings.labor_target),
    wasteTarget: Number(dbSettings.waste_target), varTarget: Number(dbSettings.var_target),
    rentPct: Number(dbSettings.rent_pct || 0), coffeePct: Number(dbSettings.coffee_pct || 0), ccPct: dbSettings.cc_processing_pct != null ? Number(dbSettings.cc_processing_pct) : null, countDay: dbSettings.count_day,
    wasteMode: dbSettings.waste_mode || 'daily',
    vendors: vendorNames, locations: dbSettings.locations || [],
    expenseCategories: dbSettings.expense_categories || [],
    salesCategories: dbSettings.sales_categories || null,
  } : null;

  // --- Sales → salesByDate + salesMeta (separate, so salesByDate shape is unchanged) ---
  let salesByDate = null;
  let salesMeta = {}; // { "2024-03-30": { source: 'toast', synced_at: '...', needs_review: true, review_flags: [...] } }
  if (dbSales?.length) {
    salesByDate = {};
    for (const r of dbSales) {
      if (!salesByDate[r.date]) salesByDate[r.date] = {};
      salesByDate[r.date][r.category] = Number(r.amount) > 0 ? String(r.amount) : '';
      // Build meta from the first toast-sourced row per date (all rows for a date share the same meta)
      if (r.source === 'toast' && !salesMeta[r.date]) {
        salesMeta[r.date] = { source: 'toast', synced_at: r.synced_at, needs_review: r.needs_review, review_flags: r.review_flags };
      }
    }
  }

  // --- Wholesale → wholesaleByDate: { "2026-05-31": 450.00 } ---
  // Only finalized + logged sales count. Revenue attributed to each line item's item_date (fallback: sale_date).
  let wholesaleByDate = {};
  if (dbWholesale?.length) {
    for (const sale of dbWholesale) {
      if (sale.status !== 'finalized' || !sale.logged_to_revenue) continue;
      const items = sale.wholesale_sale_items || [];
      if (items.length > 0) {
        const saleTax = Number(sale.tax || 0);
        const lineTotal = items.reduce((s, li) => s + Number(li.amount || 0), 0);
        let taxAllocated = 0;
        for (let i = 0; i < items.length; i++) {
          const li = items[i];
          const d = li.item_date || sale.sale_date;
          const amt = Number(li.amount || 0);
          // Last item gets remaining tax to avoid rounding drift
          const taxShare = (saleTax && lineTotal > 0)
            ? (i === items.length - 1 ? saleTax - taxAllocated : Math.round(saleTax * amt / lineTotal * 100) / 100)
            : 0;
          taxAllocated += taxShare;
          wholesaleByDate[d] = (wholesaleByDate[d] || 0) + amt + taxShare;
        }
        // If tax exists but all lines are zero, put tax on sale_date
        if (saleTax > 0 && lineTotal === 0) {
          wholesaleByDate[sale.sale_date] = (wholesaleByDate[sale.sale_date] || 0) + saleTax;
        }
      } else {
        // No line items — fall back to sale header total on sale_date
        const d = sale.sale_date;
        wholesaleByDate[d] = (wholesaleByDate[d] || 0) + Number(sale.total || 0);
      }
    }
  }

  // --- Labor → laborByDate ---
  let laborByDate = null;
  if (dbLabor?.length) {
    laborByDate = {};
    for (const r of dbLabor) {
      laborByDate[r.date] = {
        labor: Number(r.labor) > 0 ? String(r.labor) : '',
        cash: Number(r.cash_received) > 0 ? String(r.cash_received) : '',
        notes: r.daily_notes || '', weather: r.weather || '',
        voided: r.voided || false, voidReason: r.void_reason || '',
        laborSource: r.labor_source || 'manual',
      };
    }
  }

  // --- Waste Log → array matching app shape ---
  let wasteLog = null;
  if (dbWaste?.length) {
    wasteLog = dbWaste.map(r => ({
      id: r.id, itemId: r.item_id, itemName: r.item_name, type: r.item_type,
      unit: r.unit, qty: Number(r.qty), reason: r.reason, date: r.date, time: r.time || '',
    }));
  }

  // --- Purchases ---
  let purchases = null;
  if (dbPurchases?.length) {
    const puInvIds = [...new Set(dbPurchases.map(r => r.invoice_id).filter(Boolean))];
    const puInvMap = new Map();
    for (let i = 0; i < puInvIds.length; i += 200) {
      const batch = puInvIds.slice(i, i + 200);
      const { data: invRows } = await supabase.from('invoices').select('id,invoice_number,grand_total').in('id', batch);
      if (invRows) for (const r of invRows) puInvMap.set(r.id, r);
    }
    purchases = dbPurchases.map(r => {
      const inv = puInvMap.get(r.invoice_id);
      return {
        id: r.id, vendor: r.vendor, amt: Number(r.amount), date: r.date, category: r.category || 'Food', invoiceId: r.invoice_id || null,
        invoiceTotal: inv?.grand_total != null ? Number(inv.grand_total) : (r.invoices?.grand_total != null ? Number(r.invoices.grand_total) : null),
        invoiceNumber: inv?.invoice_number || r.invoices?.invoice_number || null,
      };
    });
  }

  // --- Other Expenses (from categorized_expenses) ---
  let otherExp = null;
  if (dbOtherExp?.length) {
    const expInvIds = [...new Set(dbOtherExp.map(r => r.source_invoice_id).filter(Boolean))];
    const invNumMap = new Map();
    for (let i = 0; i < expInvIds.length; i += 200) {
      const batch = expInvIds.slice(i, i + 200);
      const { data: invRows } = await supabase.from('invoices').select('id,invoice_number').in('id', batch);
      if (invRows) for (const r of invRows) invNumMap.set(r.id, r.invoice_number);
    }
    otherExp = dbOtherExp.map(r => ({
      id: r.id, vendor: r.vendor || null, description: r.description || '', amt: Number(r.amount), date: r.date, category: r.category || null, sourceInvoiceId: r.source_invoice_id || null, isEdited: r.is_edited || false, scannedSnapshot: r.scanned_snapshot || null, invoiceItemNumber: r.invoice_item_number || null, invoiceLineIndex: r.invoice_line_index ?? null, invoiceNumber: invNumMap.get(r.source_invoice_id) || null,
    }));
  }

  // --- Inventory Counts (date-scoped for current salesDate) ---
  const invCountRows = (dbInvCounts || []).map(r => ({
    ingredientId: r.ingredient_id,
    cases: Number(r.cases) > 0 ? String(r.cases) : '',
    open: Number(r.open_qty) > 0 ? String(r.open_qty) : '',
  }));

  // --- Inventory Last Counted (per-ingredient max date, client-side grouped) ---
  const invLastCounted = {};
  for (const r of (dbInvLastCounted || [])) {
    if (!invLastCounted[r.ingredient_id] || r.date > invLastCounted[r.ingredient_id]) {
      invLastCounted[r.ingredient_id] = r.date;
    }
  }

  return {
    ingredients, batches, menuItems, templates, fixedCosts, settings,
    salesByDate, salesMeta, wholesaleByDate, laborByDate, wasteLog, purchases, otherExp, invCountRows, invLastCounted, userPerms, canManageTeam,
    toastEnabled,
    orgId, userRole, allOrgs, onboardingComplete: allOrgs.find(o => o.id === orgId)?.onboarding_complete ?? true,
  };
}

export async function loadCustomDeductions(supabase, orgId) {
  const { data } = await supabase
    .from('custom_deductions')
    .select('id,name,type,value,category,is_active,sort_order')
    .eq('org_id', orgId)
    .eq('is_active', true)
    .order('sort_order', { ascending: true });
  return data || [];
}

export async function getInventoryCountsForRange(supabase, orgId, rangeEnd) {
  const { data } = await supabase
    .from('inventory_counts')
    .select('date,ingredient_id,cases,open_qty,unit_cost,ppc_at_count')
    .eq('org_id', orgId)
    .lte('date', rangeEnd)
    .order('date', { ascending: false })
    .limit(20000);
  return data || [];
}
